import { useMemo, useState, useRef, useCallback } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { getCareTimeline } from '../../utils/carePhaseUtils';
import CareTimelineSection from './CareTimelineSection';
import DataDisclaimerBadge from '../common/DataDisclaimerBadge';

/**
 * PflegekalenderView — shown when dashboardTab === 'pflegekalender'.
 *
 * Mobile additions:
 *   - Responsive padding (px-4 py-4 on mobile, px-8 py-8 on desktop)
 *   - Offline banner (sticky, gray-100) when navigator.onLine = false
 *   - Pull-to-refresh gesture (native touch events, > 60px pull triggers reload)
 *   - Sticky section headers handled by CareTimelineSection on mobile
 */
const PflegekalenderView = () => {
  const { placedPlants, allPlants } = useDashboard();
  const { isMobile } = useBreakpoint();
  const currentMonth = new Date().getMonth() + 1;

  const isOnline = useOnlineStatus();
  const [refreshing, setRefreshing] = useState(false);
  const touchStartYRef = useRef(null);
  const [pullDelta, setPullDelta] = useState(0);

  const placedPlantIds = useMemo(
    () =>
      placedPlants
        .map((p) => (typeof p === 'object' && p !== null ? (p.id ?? p.plantId ?? null) : p))
        .filter(Boolean),
    [placedPlants]
  );

  const timeline = useMemo(
    () => getCareTimeline(placedPlantIds, allPlants, currentMonth),
    [placedPlantIds, allPlants, currentMonth]
  );

  const hasNoPlants = placedPlantIds.length === 0;

  const onTouchStart = useCallback((e) => {
    touchStartYRef.current = e.touches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (touchStartYRef.current === null) return;
    const delta = e.touches[0].clientY - touchStartYRef.current;
    if (delta > 0) setPullDelta(Math.min(70, delta));
  }, []);

  const onTouchEnd = useCallback(() => {
    if (pullDelta > 60 && !refreshing) {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 1000);
    }
    setPullDelta(0);
    touchStartYRef.current = null;
  }, [pullDelta, refreshing]);

  return (
    <div
      className="max-w-[800px] mx-auto py-4 px-4 md:py-8 md:px-8 flex flex-col gap-4 md:gap-6"
      onTouchStart={isMobile ? onTouchStart : undefined}
      onTouchMove={isMobile ? onTouchMove : undefined}
      onTouchEnd={isMobile ? onTouchEnd : undefined}
    >
      {/* Offline Banner */}
      {!isOnline && (
        <div
          className="sticky top-0 z-20 bg-gray-100 text-gray-900 text-xs px-4 py-3 -mx-4 md:-mx-8"
          role="status"
        >
          Keine Verbindung. Die Daten werden aktualisiert, sobald du wieder online bist.
        </div>
      )}

      {/* Pull-to-refresh indicator */}
      {isMobile && (refreshing || pullDelta > 20) && (
        <div className="flex justify-center py-1">
          <span className={`text-xs text-gray-400 ${refreshing ? 'animate-pulse' : ''}`}>
            {refreshing
              ? 'Wird aktualisiert …'
              : pullDelta > 50
              ? 'Loslassen zum Aktualisieren'
              : 'Herunterziehen zum Aktualisieren'}
          </span>
        </div>
      )}

      {hasNoPlants ? (
        <p className="text-[13px] text-gray-500">
          Sobald du Pflanzen angelegt hast, siehst du hier deine Pflegephasen.
        </p>
      ) : (
        <>
          <CareTimelineSection
            title="Aktuell relevant"
            groups={timeline.current}
            emptyText="Aktuell gibt es hier nichts zu beachten."
          />
          <CareTimelineSection
            title="Demnächst"
            groups={timeline.upcoming}
            emptyText="Für die nächsten Wochen ist nichts Zusätzliches vorgemerkt."
          />
          <CareTimelineSection
            title="Später"
            groups={timeline.later}
            emptyText="Für die kommenden Monate ist nichts Zusätzliches vorgemerkt."
          />
        </>
      )}

      {/* Premium-Teaser (Trigger-Kategorie B: Zukunftsblick) */}
      <div className="bg-emerald-50 rounded-xl p-4">
        <p className="text-[13px] text-gray-700">
          Mit dem Pflegekalender siehst du frühzeitig, was in den kommenden Wochen relevant wird.
          Möchtest du, dass Grünraum dich automatisch informiert?
        </p>
      </div>

      {/* Haftungs-Disclaimer (#286): durchgängig sichtbar, nicht dismissible */}
      <DataDisclaimerBadge variant="care" />
    </div>
  );
};

export default PflegekalenderView;
